import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FileText, Plus } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useAuth } from '@/auth/useAuth'
import { useBills } from '@/features/ap/queries'
import type { BillListItem, BillStatus } from '@/features/ap/types'
import { formatDate } from '@/lib/locale'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { useToast } from '@/hooks/useToast'

const STATUS_VARIANT: Record<BillStatus, 'default' | 'secondary' | 'danger' | 'success' | 'warning'> = {
  draft: 'secondary',
  pending_approval: 'warning',
  approved: 'default',
  scheduled: 'default',
  partially_paid: 'default',
  paid: 'success',
  overdue: 'danger',
  void: 'secondary',
  rejected: 'danger',
}

function fmtMoney(v: string | number, currency: string) {
  const n = typeof v === 'number' ? v : parseFloat(v || '0')
  return formatInvoiceMoney(n, currency)
}

export function BillsListPage() {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState<BillStatus | ''>('')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useBills(status ? { status } : undefined)
  const rows = useMemo(() => {
    const items = data?.bills ?? []
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(i =>
      i.bill_number.toLowerCase().includes(q)
      || (i.vendor_display_name || '').toLowerCase().includes(q)
      || (i.vendor_invoice_number || '').toLowerCase().includes(q)
    )
  }, [data, search])

  const columns = useMemo(() => [
    { accessorKey: 'bill_number', header: 'Bill #',
      cell: ({ row }: { row: { original: BillListItem } }) => (
        <div className="flex flex-col">
          <Link to={`/invoicing/bills/${row.original.id}`} className="font-medium text-primary hover:underline">
            {row.original.bill_number}
          </Link>
          {row.original.vendor_invoice_number ? (
            <span className="text-[0.7rem] text-muted-foreground">Vendor inv: {row.original.vendor_invoice_number}</span>
          ) : null}
        </div>
      ),
    },
    { accessorKey: 'vendor_display_name', header: 'Vendor' },
    { accessorKey: 'issue_date', header: 'Issue',
      cell: ({ row }: { row: { original: BillListItem } }) => formatDate(row.original.issue_date),
    },
    { accessorKey: 'due_date', header: 'Due',
      cell: ({ row }: { row: { original: BillListItem } }) => formatDate(row.original.due_date),
    },
    { accessorKey: 'total', header: 'Total',
      cell: ({ row }: { row: { original: BillListItem } }) => fmtMoney(row.original.total, row.original.currency),
    },
    { accessorKey: 'status', header: 'Status',
      cell: ({ row }: { row: { original: BillListItem } }) => (
        <Badge variant={STATUS_VARIANT[row.original.status]}>{row.original.status.replace(/_/g, ' ')}</Badge>
      ),
    },
  ], [])

  const canCreate = hasPermission('invoicing.bills.create')

  // Posting banner - shown when there are unposted bills (manual mode
  // queues bills here until the user clicks Post Now).
  const posting = usePostingStatus()
  const postSelectedMut = usePostSelected()
  const { toast } = useToast()
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)
  const unpostedBills = posting.data?.unpostedBills ?? 0

  const billItems = useMemo<PostingSelectorItem[]>(() => {
    const items = unpostedPreview.data?.bills ?? []
    return items.map((r) => ({
      id: r.id,
      cells: [
        formatDate(r.billDate),
        <span className="font-mono text-xs">{r.billNumber || `BILL #${r.id}`}</span>,
        r.vendorName || '-',
        r.status ? <Badge variant="secondary" className="capitalize">{String(r.status).replace(/_/g, ' ')}</Badge> : '-',
        formatInvoiceMoney(r.total, r.currency || 'USD'),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
    }))
  }, [unpostedPreview.data])

  const billColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Bill #', width: 'w-32' },
    { header: 'Vendor' },
    { header: 'Status', width: 'w-32' },
    { header: 'Total', align: 'right' as const, width: 'w-28' },
  ]

  const runPostBills = async (selection: Record<string, number[]>) => {
    const ids = selection.bills ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'bills', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? 0} bill${(r.posted ?? 0) === 1 ? '' : 's'}.`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: err instanceof Error ? err.message : 'Try again.',
      })
    }
  }

  return (
    <Page
      title="Bills"
      description="Incoming vendor bills. New bills wait for an explicit Post Now click unless you switch bills to auto-post in Company → Posting Config. Approval policies still apply on top."
      actions={canCreate ? (
        <Button onClick={() => navigate('/invoicing/bills/new')}><Plus className="mr-2 size-4" /> New bill</Button>
      ) : undefined}
    >
      <PostingBanner
        count={unpostedBills}
        label="bills"
        singularLabel="bill"
        source="bills"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending bills' : 'Post bills to GL'}
        description={selectorReadOnly
          ? 'These bills are queued to post. Click Post Now to choose which to post.'
          : 'Tick the bills you want to post. Posting only affects the rows you select - invoices, accruals, and other sources are untouched.'}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        tabs={[
          {
            key: 'bills',
            label: 'Bills',
            items: billItems,
            columns: billColumns,
            emptyMessage: 'No bills are pending GL posting.',
          },
        ]}
        onPost={runPostBills}
      />

      <Toolbar align="start" className="mb-2">
        <FormField label="" placeholder="Search bill # or vendor" value={search} onChange={e => setSearch(e.currentTarget.value)} className="w-64" />
        <select
          value={status}
          onChange={e => setStatus(e.currentTarget.value as BillStatus | '')}
          className="h-10 rounded-md border bg-card px-3 text-sm"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending approval</option>
          <option value="approved">Approved</option>
          <option value="partially_paid">Partially paid</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="rejected">Rejected</option>
          <option value="void">Void</option>
        </select>
      </Toolbar>
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : (
        <DataTable
          columns={columns as never}
          rows={rows}
          empty={<EmptyState icon={FileText} title="No bills yet" description={canCreate ? 'Enter your first vendor bill.' : 'No bills match your filters.'}
            action={canCreate ? <Button onClick={() => navigate('/invoicing/bills/new')}><Plus className="mr-2 size-4" />New bill</Button> : null} />}
        />
      )}
    </Page>
  )
}
